<template>
  <v-row class="fill-height" no-gutters>
    <v-col cols="12">
      <card-title-row
        class="pl-0"
        :class="isCardInfoPrint ? 'pb-5' : 'py-5'"
        translation-path="sidebar.congestionAnalysis.title"
        :has-buttons="false"
      >
        <template #subtitle>
          <italic-subheader
            style="font-size: 12px !important;"
            class="text-left"
            translation-path="sidebar.congestionAnalysis.subtitle"
          />
        </template>
      </card-title-row>
      <sidebar-row v-for="(input, idx) in inputs" :key="idx">
        <v-col cols="8" class="pb-3 px-0">
          <v-tooltip bottom max-width="300">
            <template #activator="{ on }">
              <span v-on="on">
                <data-input v-model="sewerLayers[input.name]" v-bind="input" />
              </span>
            </template>
            {{ getTooltip(sewerLayers[input.name] ? sewerLayers[input.name].name : '') }}
          </v-tooltip>
        </v-col>
      </sidebar-row>
      <congestion-analysis-tool
        :sewer-terminals-data-source="sewerTerminalsDataSource"
        :sewer-layers-ids="sewerLayersIds"
        :all-sewers-added="allSewersAdded"
      />
    </v-col>
  </v-row>
</template>

<script>
import { get } from 'vuex-pathify';

import CardTitleRow from '@/components/CardTitleRow';
import ItalicSubheader from '@/components/ItalicSubheader';
import SidebarRow from '@/components/SidebarRow';
import DataInput from '@/components/DataInput';

export default {
  name: 'CongestionAnalysis',
  components: {
    CardTitleRow,
    ItalicSubheader,
    SidebarRow,
    DataInput,
    CongestionAnalysisTool: () => import('@/components/CongestionAnalysisTool'),
  },
  data: () => ({
    sewerLayers: {
      currentPipesLayer: undefined,
      currentManholesLayer: undefined,
      currentTerminalsLayer: undefined,
    },
  }),
  computed: {
    projectElements: get('layers/project@layers'),
    sewerPipesDataSource: get('admin/modulesMapping@sewer.sewer_pipes_data_source'),
    sewerManholesDataSource: get('admin/modulesMapping@sewer.sewer_manholes_data_source'),
    sewerTerminalsDataSource: get('admin/modulesMapping@sewer.sewer_terminals_data_source'),
    isCardInfoPrint: get('tools/toolStatus@isCardInfoPrintActive'),
    currentPipesLayerId() {
      return this.sewerLayers.currentPipesLayer?.id;
    },
    currentManholesLayerId() {
      return this.sewerLayers.currentManholesLayer?.id;
    },
    currentTerminalsLayerId() {
      return this.sewerLayers.currentTerminalsLayer?.id;
    },
    sewerLayersIds() {
      return {
        pipe_layer: this.currentPipesLayerId,
        manhole_layer: this.currentManholesLayerId,
        terminal_layer: this.currentTerminalsLayerId,
      };
    },
    inputs() {
      return [
        {
          name: 'currentPipesLayer',
          dataType: { name: 'select' },
          disabled: !this.allSewersAdded,
          items: this.sewerPipesLayers,
          itemText: 'name',
          label: this.$i18n.t('sidebar.congestionAnalysis.sewerPipesLayer'),
          hideDetails: true,
          returnObject: true,
        },
        {
          name: 'currentManholesLayer',
          dataType: { name: 'select' },
          disabled: !this.allSewersAdded,
          items: this.sewerManholesLayers,
          itemText: 'name',
          label: this.$i18n.t('sidebar.congestionAnalysis.sewerManholesLayer'),
          hideDetails: true,
          returnObject: true,
        },
        {
          name: 'currentTerminalsLayer',
          dataType: { name: 'select' },
          disabled: !this.allSewersAdded,
          items: this.sewerTerminalsLayers,
          itemText: 'name',
          label: this.$i18n.t('sidebar.congestionAnalysis.sewerTerminalsLayer'),
          hideDetails: true,
          returnObject: true,
        },
      ];
    },
    projectLayers() {
      return this.$_getFlatGroupsLayers(this.projectElements).layers;
    },
    sewerPipesLayers() {
      this.$forceUpdate();
      return this.projectLayers?.filter(layer => layer.data_source_name === this.sewerPipesDataSource);
    },
    sewerManholesLayers() {
      this.$forceUpdate();
      return this.projectLayers?.filter(layer => layer.data_source_name === this.sewerManholesDataSource);
    },
    sewerTerminalsLayers() {
      this.$forceUpdate();
      return this.projectLayers?.filter(layer => layer.data_source_name === this.sewerTerminalsDataSource);
    },
    sewerPipesDataSourceAdded() {
      return this.sewerPipesLayers?.length > 0;
    },
    sewerManholesDataSourceAdded() {
      return this.sewerManholesLayers?.length > 0;
    },
    sewerTerminalsDataSourceAdded() {
      return this.sewerTerminalsLayers?.length > 0;
    },
    allSewersAdded() {
      return this.sewerManholesDataSourceAdded && this.sewerPipesDataSourceAdded && this.sewerTerminalsDataSourceAdded;
    },
  },
  mounted() {
    this.$watch(
      vm => {
        return {
          pipesLayers: vm.sewerPipesLayers,
          manholesLayers: vm.sewerManholesLayers,
          terminalsLayers: vm.sewerTerminalsLayers,
        };
      },
      nV => {
        for (const key of Object.keys(nV)) {
          const layers = nV[key];
          if (!layers) {
            continue;
          }
          const currentLayerIdVarName = `current${this.$_capitalize(key).replace('Layers', 'LayerId')}`;
          if (layers.find(layer => layer.id === this[currentLayerIdVarName])) {
            continue;
          } else {
            const currentLayersVarName = currentLayerIdVarName.replace('LayerId', 'Layer');
            this.sewerLayers[currentLayersVarName] = layers[0];
          }
        }
      },
      {
        immediate: true,
        deep: true,
      }
    );
  },
  methods: {
    getTooltip(name) {
      return this.allSewersAdded ? name : this.$i18n.t('sidebar.congestionAnalysis.noAllSewersSelected');
    },
  },
};
</script>
